import { lazy, type ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './layouts/DashboardLayout';

const PlaylistManagerPage = lazy(() => import('./pages/dashboard/PlaylistManagerPage'));
const EditorModulePage = lazy(() => import('./pages/dashboard/EditorModulePage'));
const BasicEditorPage = lazy(() => import('./pages/dashboard/editors/BasicEditorPage'));
const NumberEditorPage = lazy(() => import('./pages/dashboard/editors/NumberEditorPage'));
const LogoEditorPage = lazy(() => import('./pages/dashboard/editors/LogoEditorPage'));
const EpgEditorPage = lazy(() => import('./pages/dashboard/editors/EpgEditorPage'));
const CategoryEditorPage = lazy(() => import('./pages/dashboard/editors/CategoryEditorPage'));
const ManualUpdaterPage = lazy(() => import('./pages/dashboard/editors/ManualUpdaterPage'));
const RecycleBinPage = lazy(() => import('./pages/dashboard/editors/RecycleBinPage'));
const UserManagePage = lazy(() => import('./pages/dashboard/UserManagePage'));
const UserNewPage = lazy(() => import('./pages/dashboard/UserNewPage'));
const AutoSourcesPage = lazy(() => import('./pages/dashboard/AutoSourcesPage'));
const AutoNewPage = lazy(() => import('./pages/dashboard/AutoNewPage'));
const MovieBasicEditorPage = lazy(() => import('./pages/dashboard/editors/vod/MovieBasicEditorPage'));
const SeriesBasicEditorPage = lazy(() => import('./pages/dashboard/editors/vod/SeriesBasicEditorPage'));
const VodCoverEditorPage = lazy(() => import('./pages/dashboard/editors/vod/VodCoverEditorPage'));
const VodInfoEditorPage = lazy(() => import('./pages/dashboard/editors/vod/VodInfoEditorPage'));
const VodCategoryEditorPage = lazy(() => import('./pages/dashboard/editors/vod/VodCategoryEditorPage'));
const VodUpdaterPage = lazy(() => import('./pages/dashboard/editors/vod/VodUpdaterPage'));
const VodRecycleEditorPage = lazy(() => import('./pages/dashboard/editors/vod/VodRecycleEditorPage'));

function ProtectedRoute({ children }: { children: ReactElement }) {
  const { ready, token } = useAuth();
  if (!ready) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const { ready, token } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={ready && token ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route
        path="/register"
        element={ready && token ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
      />
      <Route
        path="/forgot-password"
        element={ready && token ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={ready && token ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="playlist-manager" replace />} />
        <Route path="playlist-manager" element={<PlaylistManagerPage />} />
        <Route path="channel-basic" element={<BasicEditorPage />} />
        <Route path="channel-number" element={<NumberEditorPage />} />
        <Route path="channel-logo" element={<LogoEditorPage />} />
        <Route path="channel-epg" element={<EpgEditorPage />} />
        <Route path="channel-category" element={<CategoryEditorPage />} />
        <Route path="channel-manual" element={<ManualUpdaterPage />} />
        <Route path="channel-recycle" element={<RecycleBinPage />} />
        <Route path="user-manage" element={<UserManagePage />} />
        <Route path="user-new" element={<UserNewPage />} />
        <Route path="auto-sources" element={<AutoSourcesPage />} />
        <Route path="auto-new" element={<AutoNewPage />} />
        <Route path="movie-basic" element={<MovieBasicEditorPage />} />
        <Route path="movie-cover" element={<VodCoverEditorPage kind="movies" />} />
        <Route path="movie-info" element={<VodInfoEditorPage kind="movies" />} />
        <Route path="movie-category" element={<VodCategoryEditorPage kind="movies" />} />
        <Route path="movie-updater" element={<VodUpdaterPage kind="movies" />} />
        <Route path="movie-recycle" element={<VodRecycleEditorPage kind="movies" />} />
        <Route path="series-basic" element={<SeriesBasicEditorPage />} />
        <Route path="series-cover" element={<VodCoverEditorPage kind="series" />} />
        <Route path="series-info" element={<VodInfoEditorPage kind="series" />} />
        <Route path="series-category" element={<VodCategoryEditorPage kind="series" />} />
        <Route path="series-updater" element={<VodUpdaterPage kind="series" />} />
        <Route path="series-recycle" element={<VodRecycleEditorPage kind="series" />} />
        <Route path=":pageKey" element={<EditorModulePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
